import { Controller } from "@hotwired/stimulus";
import initializeRaters from "../components/star-rater";
import { initializeRadioButtons } from "../components/square-radio-button";
import { fadeOut } from "../components/fade-effects";
import getCsrfToken from "../utils/csrf";

export default class extends Controller {
  connect() {
    initializeRaters({ callback: this.onRaterClick });
    initializeRadioButtons();
    this.scrollToAnchorIfPresent();
  }

  prepareFeedbackData(type) {
    const get = (id) => document.getElementById(id)?.value ?? "";

    const data = {
      public_review: get("public_review"),
      private_review: get("private_review"),
      comment: get("comment"),
    };

    const addScore = (key) => (data[key] = this.fetchScore(key));
    const addAnswerWithDetails = (key) => {
      const val = this.fetchAnswer(key);
      data[key] = val;
      if (val == "false") data[`${key}_details`] = get(`${key}_details`);
    };

    if (type === "landlord") {
      addScore("brand_score");
      addScore("respectfulness_score");
      addScore("cooperation_score");

      addAnswerWithDetails("concept_fit");
      addAnswerWithDetails("booking_success_for_landlord");
      addAnswerWithDetails("work_again_with_tenant");
    } else if (type === "tenant") {
      addScore("landlord_score");
      addScore("location_score");
      addScore("space_score");

      addAnswerWithDetails("good_footfall");
      addAnswerWithDetails("booking_success_for_tenant");
      addAnswerWithDetails("work_again_with_landlord");
    }

    return data;
  }

  fetchScore(elementId) {
    return document.getElementById(elementId).getAttribute("data-rating");
  }

  fetchAnswer(elementId) {
    return document.getElementById(elementId).getAttribute("data-value");
  }

  showNotification(message, kind) {
    const errorsContainer = document.getElementById("feedback_errors");
    const notifier = document.createElement("div");
    let notifierClass = "alert";
    switch (kind) {
      case "warning":
        notifierClass = "alert alert-warning";
        break;
      case "success":
        notifierClass = "alert alert-success";
        break;
    }

    notifier.className = notifierClass;
    notifier.innerHTML = `<button class="close" data-dismiss="alert">×</button>${message}`;
    errorsContainer.appendChild(notifier);
    fadeOut({ element: notifier, remove: true });
  }

  onRaterClick() {
    let votedRates = 0;
    let totalScore = 0.0;
    document.querySelectorAll(".star-rater").forEach((rater) => {
      const score = rater.getAttribute("data-rating");
      if (!score) return;

      votedRates += 1;
      totalScore += parseInt(score);
    });

    if (votedRates) totalScore /= votedRates;
    document.getElementById("total_score").innerText = parseFloat(totalScore).toFixed(2);
  }

  scrollUp() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  // Get hash from url to scroll to anchor element considering scrollable containers
  scrollToAnchorIfPresent() {
    const hash = window.location.hash;
    if (!hash) return;

    const elementId = hash.substring(1);
    const targetElement = document.getElementById(elementId);

    if (!targetElement) return;

    setTimeout(() => {
      const scrollableContainer = this.findScrollableContainer(targetElement);

      if (scrollableContainer) {
        const containerRect = scrollableContainer.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();

        const scrollOffset = 20;
        const scrollPosition = scrollableContainer.scrollTop + targetRect.top - containerRect.top - scrollOffset;

        scrollableContainer.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      } else {
        targetElement.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
    }, 100);
  }

  findScrollableContainer(element) {
    let parent = element.parentElement;

    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const overflow = style.overflow + style.overflowY + style.overflowX;

      if (overflow.includes('scroll') || overflow.includes('auto')) {
        if (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth) {
          return parent;
        }
      }

      parent = parent.parentElement;
    }

    return document.documentElement.scrollHeight > document.documentElement.clientHeight
      ? document.documentElement
      : document.body;
  }

  toggleQuestionDetails(event) {
    const questionId = event.currentTarget.id;

    const questionDetails = document.getElementById(`${questionId}_details`);
    if (!questionDetails) return;

    if (this.fetchAnswer(questionId) === "false") {
      questionDetails.classList.remove("hidden");
    } else {
      questionDetails.classList.add("hidden");
    }
  }

  save() {
    const feedbackUrl = document.getElementById("feedback_url").value;
    const feedbackType = document.getElementById("feedback_type").value;

    fetch(feedbackUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify(this.prepareFeedbackData(feedbackType)),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.errors) {
          Object.values(data.errors).forEach((message) => {
            this.showNotification(message, "warning");
          });

          return;
        }

        const totalScore = document.getElementById("total_score");
        totalScore.innerText = parseFloat(data.total_score).toFixed(2);
        this.showNotification(data.message, "success");
      });
  }
}
